/**
 * EmailPolishForm: the Email helper's "just spelling and grammar" face.
 *
 * One box for the email the writer already wrote and one button. The
 * answer is the same email back with spelling, grammar and punctuation
 * corrected and nothing else changed, with everything a drafted email
 * carries around it (the recipient off the greeting, the colleague notes,
 * billing on the Care Management face) so it saves and files like any
 * other. The Care Management / SPICE switch stays: it decides the
 * billing line and the filing door. There is no tone: nothing is
 * reworded.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { KeptWorkNotice } from '@/components/KeptWorkNotice';
import { keptWorkKey, useKeptWork } from '@/lib/keptWork';
import { useMutation } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { aiGenerateKeyOf } from '@/lib/errorCopy';
import { ErrorNotice } from '@/components/ErrorNotice';
import { Button } from '@/components/primitives/Button';
import { aiToolsApi, type ApiError, type EmailDraftResponse } from '@/lib/api';
import { EmailModeToggle, type EmailMode } from './EmailModeToggle';
import { TextTooLongNote, isTextTooLong } from './TextTooLongNote';
import { readAiInputMaxChars } from '@/lib/aiInputLimits';
import { aiToolFailureCopy, aiToolFailureOf, type AiToolFailure } from '@/lib/aiTextTooLong';
import { useInvalidateOnCeilingRefusal } from '@/hooks/useInvalidateOnCeilingRefusal';

export interface EmailPolishFormProps {
  hasDraft?: boolean;
  tenantSlug: string;
  mode: EmailMode;
  onModeChange: (mode: EmailMode) => void;
  /** The mode is fixed (a client is chosen, or the viewer is SPICE-only). */
  modeLocked?: boolean;
  onGeneratingChange?: (generating: boolean) => void;
  onPolished: (response: EmailDraftResponse, text: string, mode: EmailMode) => void;
  /** The longest email Polish takes (characters, after trimming): the
   *  email helper's input ceiling, read by the page; the factory value
   *  until the page passes one. */
  maxChars?: number;
}

const LABEL_CLS = 'block text-title-sm font-semibold text-sage-800';
const TEXTAREA_CLS =
  'mt-1.5 block w-full min-h-64 rounded-[12px] border border-sage-400/20 bg-paper px-3.5 py-2.5 text-sm leading-relaxed text-ink placeholder:text-sage-300 outline-none transition-[border-color,box-shadow] duration-[var(--dur-base)] ease-quiet focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]';

export function EmailPolishForm({
  hasDraft = false,
  tenantSlug,
  mode,
  onModeChange,
  modeLocked = false,
  onGeneratingChange,
  onPolished,
  maxChars = readAiInputMaxChars(undefined, 'email'),
}: EmailPolishFormProps): ReactNode {
  const [text, setText] = useState('');
  // The last failure: a registry key, or a refusal for length with its box.
  const [failure, setFailure] = useState<AiToolFailure | null>(null);
  const onCeilingRefusal = useInvalidateOnCeilingRefusal(tenantSlug);

  // An expired sign-in keeps the pasted email until it is polished; after
  // that the page keeps the result (src/lib/keptWork.ts).
  const kept = useKeptWork<{ text: string; mode: EmailMode }>(
    keptWorkKey(tenantSlug, 'email-polish', null),
    () => (hasDraft || text.trim() === '' ? null : { text, mode }),
    (k) => {
      setText(k.text);
      if (!modeLocked) onModeChange(k.mode);
    },
  );
  useEffect(() => {
    if (hasDraft) kept.clear();
  }, [hasDraft]);

  // The text and the mode are read at the press and travel as the variables.
  const mutation = useMutation({
    mutationFn: (v: { text: string; mode: EmailMode }) =>
      aiToolsApi.draftEmail(tenantSlug, { task: 'polish', mode: v.mode, text: v.text }),
    onSuccess: (res, v) => {
      setFailure(null);
      onPolished(res, v.text, v.mode);
    },
    onError: (e: ApiError, v) => {
      onCeilingRefusal(e);
      // The polish sends one text: a refusal naming no box is about it.
      setFailure(aiToolFailureOf(e, 'email_generate', 'text', { text: v.text }, (x) => aiGenerateKeyOf(x) ?? 'unknown'));
    },
  });

  const generating = mutation.isPending;
  useEffect(() => {
    onGeneratingChange?.(generating);
  }, [generating, onGeneratingChange]);

  const trimmed = text.trim();
  const tooLong = isTextTooLong(text, maxChars);
  const canSubmit = trimmed.length > 0 && !tooLong;
  // Every box this route fences reads the email helper's ceiling.
  const failureCopy = failure === null ? null : aiToolFailureCopy(failure, () => maxChars);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ text: text.trim(), mode });
      }}
      className="space-y-[18px]"
      data-testid="email-polish-form"
    >
      {kept.restored && <KeptWorkNotice onDiscard={kept.discard} />}
      {!modeLocked && (
        <div>
          <EmailModeToggle mode={mode} onChange={onModeChange} disabled={generating} />
        </div>
      )}

      <div>
        <label htmlFor="email-polish-text" className={LABEL_CLS}>
          Your email
        </label>
        <p className="text-xs text-ink-soft mt-0.5" data-testid="email-polish-hint">
          Paste the email you wrote. Spelling, grammar and punctuation are tidied; nothing else
          changes.
        </p>
        <textarea
          id="email-polish-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className={TEXTAREA_CLS}
          disabled={generating}
        />
        {tooLong && (
          <TextTooLongNote surface="email_generate" maxChars={maxChars} kind="split" text={text} data-testid="email-polish-too-long" />
        )}
      </div>

      {failureCopy !== null && (
        <ErrorNotice surface="email_generate" code={failureCopy.key} vars={failureCopy.vars} />
      )}

      <Button
        type="submit"
        variant={hasDraft ? 'ghost' : 'primary'}
        size={hasDraft ? 'sm' : 'md'}
        disabled={!canSubmit || generating}
        loading={generating}
        icon={<Sparkles size={17} aria-hidden="true" />}
      >
        {generating ? 'Polishing…' : 'Polish'}
      </Button>
    </form>
  );
}
